/**
 * Pure math behind the generation previews: fitting and zooming an image in a
 * box (wheel zoom toward the cursor, pan, refit) and orbiting a model.
 */

/** Where the content sits in its box: top-left offset and uniform scale. */
export interface ViewTransform {
  x: number;
  y: number;
  scale: number;
}

export interface Size {
  width: number;
  height: number;
}

/** Scale that fits the whole content in the box (may enlarge, so tiny pixel art fills the area). */
export function fitScale(content: Size, box: Size, pad = 0): number {
  if (content.width <= 0 || content.height <= 0 || box.width <= 0 || box.height <= 0) return 1;
  const w = Math.max(1, box.width - pad * 2);
  const h = Math.max(1, box.height - pad * 2);
  return Math.min(w / content.width, h / content.height);
}

/** Content centred in the box at the fit scale. */
export function fitView(content: Size, box: Size, pad = 0): ViewTransform {
  const scale = fitScale(content, box, pad);
  return { scale, x: (box.width - content.width * scale) / 2, y: (box.height - content.height * scale) / 2 };
}

/** Zoom limits relative to the fit scale. */
export function zoomLimits(fit: number): { min: number; max: number } {
  return { min: fit * 0.25, max: Math.max(fit * 40, 40) };
}

/** Multiplies the scale by `factor` keeping the content point under (cx, cy) fixed. */
export function zoomAt(view: ViewTransform, cx: number, cy: number, factor: number, min: number, max: number): ViewTransform {
  const scale = Math.min(max, Math.max(min, view.scale * factor));
  const k = scale / view.scale;
  return { scale, x: cx - (cx - view.x) * k, y: cy - (cy - view.y) * k };
}

/** Wheel delta (pixels, lines or pages) to a zoom factor. */
export function wheelFactor(deltaY: number, deltaMode = 0): number {
  const px = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 400 : deltaY;
  return Math.exp(-Math.max(-300, Math.min(300, px)) * 0.0018);
}

export function panBy(view: ViewTransform, dx: number, dy: number): ViewTransform {
  return { ...view, x: view.x + dx, y: view.y + dy };
}

/** Keeps at least `keep` pixels of the content inside the box so it cannot be lost. */
export function clampPan(view: ViewTransform, content: Size, box: Size, keep = 32): ViewTransform {
  const w = content.width * view.scale;
  const h = content.height * view.scale;
  const k = Math.min(keep, w, h, box.width, box.height);
  return {
    scale: view.scale,
    x: Math.min(box.width - k, Math.max(k - w, view.x)),
    y: Math.min(box.height - k, Math.max(k - h, view.y)),
  };
}

/** True when the view still matches the fit (so a resize should refit it). */
export function isFit(view: ViewTransform, content: Size, box: Size, pad = 0, eps = 0.5): boolean {
  const fit = fitView(content, box, pad);
  return Math.abs(view.scale - fit.scale) < 1e-6 * Math.max(1, fit.scale) && Math.abs(view.x - fit.x) < eps && Math.abs(view.y - fit.y) < eps;
}

/* ------------------------------------------------------------------ */
/* Model orbit                                                         */

export interface Orbit {
  /** Turntable yaw in degrees, in -180..180. */
  angle: number;
  /** Camera pitch in degrees (negative looks down), clamped to +-80. */
  pitch: number;
}

export const DEFAULT_ORBIT: Orbit = { angle: 35, pitch: -17 };
export const ORBIT_STEP = { angle: 15, pitch: 5 } as const;

export function wrapAngle(a: number): number {
  return ((((a + 180) % 360) + 360) % 360) - 180;
}

/** Drag of (dx, dy) pixels: horizontal turns the model, vertical tilts the camera. */
export function orbitBy(orbit: Orbit, dx: number, dy: number, degPerPixel = 0.6): Orbit {
  return { angle: wrapAngle(orbit.angle + dx * degPerPixel), pitch: Math.max(-80, Math.min(80, orbit.pitch + dy * degPerPixel)) };
}

/** Snaps to the grid the server caches renders on (same rule as the server's `snapView`). */
export function snapOrbit(orbit: Orbit): Orbit {
  const a = Math.round(orbit.angle / ORBIT_STEP.angle) * ORBIT_STEP.angle;
  const p = Math.round(orbit.pitch / ORBIT_STEP.pitch) * ORBIT_STEP.pitch;
  return { angle: wrapAngle(a), pitch: Math.max(-85, Math.min(85, p)) };
}

export function sameOrbit(a: Orbit, b: Orbit): boolean {
  return a.angle === b.angle && a.pitch === b.pitch;
}
